import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { fetchUserAttributes, getCurrentUser, signIn } from 'aws-amplify/auth';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import TextFieldAuth from '../../../components/custom/TextFieldAuth';
import type { AppUser } from '../../../utils/context/useAuthContext';
import { useAuth } from '../../../utils/context/useAuthContext';
import { errorMessages } from '../../../utils/errors/auth-errors';
import { UserRole } from '../../../utils/types/types';

interface LogInFormValues {
  email: string;
  password: string;
}

const LogIn: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const from = location.state?.from || '/dashboard';

  const [isLoading, setIsLoading] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);

  const {
    handleSubmit,
    control,
    setError,
    formState: { errors },
    clearErrors,
  } = useForm<LogInFormValues>();

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const parseVerifiedAttribute = (
    attr: string | undefined,
  ): boolean | undefined => {
    if (attr === undefined) return undefined;
    return attr.toLowerCase() === 'true';
  };

  const onSubmit = async (data: LogInFormValues) => {
    setIsLoading(true);
    clearErrors();

    try {
      const { isSignedIn, nextStep } = await signIn({
        username: data.email,
        password: data.password,
      });

      if (isSignedIn) {
        const cognitoUser = await getCurrentUser();
        const attributes = await fetchUserAttributes();
        const userData: AppUser = {
          userId: cognitoUser.userId,
          email: attributes.email,
          email_verified: parseVerifiedAttribute(attributes.email_verified),
          phone_number: attributes.phone_number,
          phone_number_verified: parseVerifiedAttribute(
            attributes.phone_number_verified,
          ),
          role: attributes['custom:role'] as UserRole | undefined,
        };

        auth.login(userData);

        if (userData.role === UserRole.ADMIN) {
          navigate('/clients', { replace: true });
        } else if (userData.role === UserRole.AGENT) {
          navigate('/agent-dashboard', { replace: true });
        } else {
          navigate(from, { replace: true });
        }
      } else {
        console.warn('Sign in requires next step:', nextStep);
        throw new Error(nextStep?.signInStep || 'SignInNotComplete');
      }
    } catch (err: any) {
      console.error('Failed to log in: ', err);
      const errorCode =
        typeof err === 'string' ? err : err.name || err.message || 'default';
      const errorMessage = errorMessages[errorCode] || errorMessages.default;

      setError('password', {
        type: 'manual',
        message: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <>
      {!isMobile ? (
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            p: '25px',
            background: 'transparent',
            my: '20px',
            gap: '20px',
            borderRadius: '20px',
            width: '513px',
          }}
          autoComplete="on"
        >
          <Box
            component="img"
            src="/logo.svg"
            sx={{
              alignSelf: 'flex-end',
              maxWidth: '300px',
              height: 'auto',
            }}
          />
          <Box
            sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}
          >
            <Typography
              fontWeight={'800'}
              fontSize={'32px'}
              fontFamily={'Heebo'}
              textAlign={'right'}
              dir="rtl"
              color={theme.palette.white.main}
            >
              כבר יש לך חשבון?
              <br />
              התחבר עכשיו
            </Typography>
          </Box>

          <Controller
            name="email"
            control={control}
            defaultValue=""
            rules={{
              required: 'חובה למלא את השדה',
              pattern: {
                value:
                  /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                message: 'כתובת אימייל לא תקינה',
              },
            }}
            render={({ field }) => (
              <TextFieldAuth
                {...field}
                id="email"
                label="כתובת מייל"
                fullWidth
                autoComplete="username"
                error={!!errors.email}
                helperText={errors.email ? errors.email.message : ''}
              />
            )}
          />
          <Controller
            name="password"
            control={control}
            defaultValue=""
            rules={{ required: 'חובה למלא את השדה' }}
            render={({ field }) => (
              <TextFieldAuth
                {...field}
                id="password"
                label="סיסמה"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                autoComplete="current-password"
                compactError
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleClickShowPassword}>
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                error={!!errors.password}
                helperText={errors.password ? errors.password.message : ''}
              />
            )}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              alignItems: 'center',
              mb: '20px',
            }}
          >
            <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <Button
                sx={{
                  width: '52px',
                  height: '52px',
                  minWidth: '52px',
                  minHeight: '52px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  display: 'flex',
                  backgroundColor: '#FFFFFF33',
                  padding: 0,
                  borderRadius: '8px',
                }}
                type="submit"
                disabled={isLoading}
              >
                <img
                  alt="auth"
                  src="/icons/auth.svg"
                  width={'22.3px'}
                  height={'25.36px'}
                />
              </Button>
              <Typography
                fontSize="16px"
                fontWeight="700"
                fontFamily={'AlmoniDL'}
                color={theme.palette.white.main}
              >
                כניסה
              </Typography>
            </Box>

            <Typography
              sx={{ cursor: 'pointer' }}
              color={theme.palette.green.light}
              onClick={() => {
                navigate('/reset');
              }}
            >
              ?שכחת סיסמה
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',

              justifyContent: 'flex-start',
              gap: '5px',
              width: '100%',
            }}
          >
            <Stack spacing={3} sx={{ alignItems: 'flex-start' }}>
              <Stack
                onClick={() => navigate('/otp')}
                sx={{
                  border: '1px solid #B6F828',
                  borderRadius: '14px',
                  px: '20px',
                  py: '10px',
                  mb: '20px',
                  cursor: 'pointer',
                }}
              >
                <Typography
                  fontSize="18px"
                  fontWeight="700"
                  fontFamily={'AlmoniDL'}
                  color="#B6F828"
                >
                  שילחו לי סיסמה חד פעמית
                </Typography>
              </Stack>
              <Stack
                spacing={0.5}
                direction="row-reverse"
                sx={{ justifyContent: 'flex-start' }}
              >
                <Typography
                  variant="body1"
                  fontWeight="fontWeightMedium"
                  fontSize={16}
                  textAlign="left"
                  dir="rtl"
                  color={theme.palette.blue.main}
                >
                  לא רשום עדיין?
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="fontWeightMedium"
                  fontSize={16}
                  color={theme.palette.blue.main}
                  textAlign="left"
                  sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={() => {
                    navigate('/signup');
                  }}
                >
                  להרשמה
                </Typography>
              </Stack>
            </Stack>
          </Box>
        </Box>
      ) : (
        <Stack
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            pt: '25px',
            px: '40px',
          }}
        >
          <Box
            component="img"
            src="/logo.svg"
            sx={{
              maxWidth: '287px',
              height: 'auto',
              mb: '40px',
            }}
          />

          <Typography
            fontWeight={'800'}
            fontSize={'32px'}
            fontFamily={'Heebo'}
            textAlign={'center'}
            dir="rtl"
            color={theme.palette.white.main}
            sx={{ mb: '20px' }}
          >
            כבר יש לך חשבון?
            <br />
            התחבר עכשיו
          </Typography>

          <Stack spacing="25px" sx={{ width: '100%', mb: '50px' }}>
            <Controller
              name="email"
              control={control}
              defaultValue=""
              rules={{
                required: 'חובה למלא את השדה',
                pattern: {
                  value:
                    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                  message: 'כתובת אימייל לא תקינה',
                },
              }}
              render={({ field }) => (
                <TextFieldAuth
                  {...field}
                  id="email"
                  label="כתובת מייל"
                  fullWidth
                  autoComplete="username"
                  error={!!errors.email}
                  helperText={errors.email ? errors.email.message : ''}
                />
              )}
            />
            <Controller
              name="password"
              control={control}
              defaultValue=""
              rules={{ required: 'חובה למלא את השדה' }}
              render={({ field }) => (
                <TextFieldAuth
                  {...field}
                  id="password"
                  label="סיסמה"
                  type={showPassword ? 'text' : 'password'}
                  fullWidth
                  autoComplete="current-password"
                  compactError
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleClickShowPassword}>
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  error={!!errors.password}
                  helperText={errors.password ? errors.password.message : ''}
                />
              )}
            />
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{
              width: '100%',
              alignItems: 'center',
              mb: '20px',
            }}
          >
            <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <Button
                sx={{
                  width: '52px',
                  height: '52px',
                  minWidth: '52px',
                  minHeight: '52px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  display: 'flex',
                  backgroundColor: '#B6F829',
                  padding: 0,
                  borderRadius: '8px',
                }}
                type="submit"
                disabled={isLoading}
              >
                <img
                  alt="auth"
                  src="/icons/auth.svg"
                  width={'22.3px'}
                  height={'25.36px'}
                />
              </Button>
              <Typography
                fontSize="16px"
                fontWeight="700"
                fontFamily={'AlmoniDL'}
                color={theme.palette.white.main}
              >
                כניסה
              </Typography>
            </Box>

            <Typography
              sx={{ cursor: 'pointer' }}
              color={theme.palette.green.light}
              onClick={() => {
                navigate('/reset');
              }}
            >
              ?שכחת סיסמה
            </Typography>
          </Stack>
          <Typography
            fontSize="18px"
            fontWeight="700"
            fontFamily={'AlmoniDL'}
            color="#FFFFFF"
            sx={{ mb: '20px' }}
          >
            — או בקשת סיסמה חד פעמית —
          </Typography>
          <Stack
            onClick={() => navigate('/otp')}
            sx={{
              border: '1px solid #B6F828',
              borderRadius: '14px',
              px: '20px',
              py: '10px',
              mb: '20px',
            }}
          >
            <Typography
              fontSize="18px"
              fontWeight="700"
              fontFamily={'AlmoniDL'}
              color="#B6F828"
            >
              שילחו לי סיסמה חד פעמית
            </Typography>
          </Stack>
          <Stack direction="row-reverse" spacing={1}>
            <Typography
              variant="body1"
              fontWeight="fontWeightMedium"
              fontSize={16}
              dir="rtl"
              color={theme.palette.blue.main}
            >
              לא רשום עדיין?
            </Typography>
            <Typography
              variant="body1"
              fontWeight="fontWeightMedium"
              fontSize={16}
              color={theme.palette.blue.main}
              sx={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => {
                navigate('/signup');
              }}
            >
              להרשמה
            </Typography>
          </Stack>
        </Stack>
      )}
    </>
  );
};

export default LogIn;
